import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import {
  Heart, MessageCircle, Repeat2, Zap, Bookmark as BookmarkIcon,
  MoreHorizontal, VolumeX, Pin, ExternalLink, Copy, Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { NoteContent } from '@/components/NoteContent';
import { UserAvatar, Username, Timestamp, pubkeyToNpub } from '@/components/user';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSocial } from '@/contexts/SocialContext';
import { useThreadChildren } from '@/hooks/useFeeds';
import { getTagValue, isRepost, repostedId, shortId } from '@/lib/jumble';
import { cn } from '@/lib/utils';
import { useAuthor } from '@/hooks/useAuthor';
import { getDisplayName } from '@/lib/getDisplayName';

export function noteIdToNevent(event: NostrEvent): string {
  try {
    return nip19.neventEncode({ id: event.id });
  } catch {
    return event.id;
  }
}

function ActionButton({
  icon, count, active, activeClass, onClick, label, disabled,
}: {
  icon: React.ReactNode; count?: number; active?: boolean; activeClass?: string;
  onClick?: (e: React.MouseEvent) => void; label: string; disabled?: boolean;
}) {
  return (
    <button
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'group flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40',
        active && activeClass,
      )}
    >
      <span className="rounded-full p-1.5 transition-colors group-hover:bg-accent">{icon}</span>
      {count !== undefined && count > 0 && <span className="tabular-nums">{count}</span>}
    </button>
  );
}

export function NoteActions({ event, detail = false }: { event: NostrEvent; detail?: boolean }) {
  const { user } = useCurrentUser();
  const { mutate: publish, isPending } = useNostrPublish();
  const { toggleBookmark, isBookmarked, toggleMutePubkey } = useSocial();
  const children = useThreadChildren(event.id);
  const navigate = useNavigate();
  const [reacted, setReacted] = useState(false);
  const [reposted, setReposted] = useState(false);
  const [copied, setCopied] = useState(false);

  const replyCount = children.data?.replies.length ?? 0;
  const repostCount = children.data?.reposts.length ?? 0;
  const zapTotal = children.data?.reactions
    .filter((r) => r.kind === 9735)
    .reduce((sum, r) => {
      const bolt11 = getTagValue(r, 'bolt11');
      // zap receipts don't carry amounts directly; count them
      void bolt11;
      return sum + 1;
    }, 0) ?? 0;

  const openThread = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/${noteIdToNevent(event)}`);
  };

  const react = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user || reacted) return;
    setReacted(true);
    publish(
      { kind: 7, content: '🤙', tags: [['e', event.id], ['p', event.pubkey]] },
      { onError: () => setReacted(false) },
    );
  };

  const repost = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user || reposted) return;
    setReposted(true);
    publish(
      { kind: 6, content: JSON.stringify(event), tags: [['e', event.id, '', 'mention'], ['p', event.pubkey]] },
      { onError: () => setReposted(false) },
    );
  };

  const copyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/${noteIdToNevent(event)}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex items-center justify-between pt-1" onClick={(e) => e.stopPropagation()}>
      <ActionButton
        icon={<MessageCircle className="h-4 w-4" />} count={replyCount}
        onClick={openThread} label="Reply"
      />
      <ActionButton
        icon={<Repeat2 className={cn('h-5 w-5', reposted && 'text-green-500')} />}
        count={repostCount + (reposted ? 1 : 0)}
        active={reposted} activeClass="text-green-500"
        onClick={repost} label="Repost" disabled={!user || isPending}
      />
      <ActionButton
        icon={<Heart className={cn('h-4 w-4', reacted && 'fill-red-500 text-red-500')} />}
        count={(children.data?.reactions.filter((r) => r.kind === 7).length ?? 0) + (reacted ? 1 : 0)}
        active={reacted} activeClass="text-red-500"
        onClick={react} label="Like" disabled={!user || isPending}
      />
      <ActionButton
        icon={<Zap className="h-4 w-4" />} count={zapTotal > 0 ? zapTotal : undefined}
        onClick={openThread} label="Zaps"
      />
      <div className="flex items-center">
        <button
          aria-label="Bookmark"
          onClick={(e) => { e.stopPropagation(); toggleBookmark(event.id); }}
          className={cn('rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground', isBookmarked(event.id) && 'text-amber-500')}
        >
          <BookmarkIcon className={cn('h-4 w-4', isBookmarked(event.id) && 'fill-amber-500')} />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button aria-label="More options" className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={copyLink}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy link
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); window.open(`https://njump.me/${noteIdToNevent(event)}`, '_blank', 'noopener'); }}>
              <ExternalLink className="h-4 w-4" /> Open in njump
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); toggleMutePubkey(event.pubkey); }}>
              <VolumeX className="h-4 w-4" /> Mute author
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {detail && <span className="hidden" />}
    </div>
  );
}

export function NoteCard({ event, showRepostHeader = true }: { event: NostrEvent; showRepostHeader?: boolean }) {
  const navigate = useNavigate();
  const repost = isRepost(event);
  const innerId = repostedId(event);

  if (repost && innerId && showRepostHeader) {
    return <RepostCard event={event} innerId={innerId} />;
  }

  const nevent = noteIdToNevent(event);
  return (
    <Card
      className="cursor-pointer gap-0 rounded-none border-x-0 border-t-0 p-4 shadow-none transition-colors first:border-t hover:bg-accent/30"
      onClick={() => navigate(`/${nevent}`)}
    >
      <div className="flex gap-3">
        <UserAvatar pubkey={event.pubkey} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm">
                <Username pubkey={event.pubkey} className="max-w-[180px]" />
            <Timestamp createdAt={event.created_at} />
          </div>
          <NoteContent event={event} className="mt-1 text-[15px] leading-relaxed break-words" />
          <NoteActions event={event} />
        </div>
      </div>
    </Card>
  );
}

function RepostCard({ event, innerId }: { event: NostrEvent; innerId: string }) {
  const navigate = useNavigate();
  const inner = useInnerEvent(event);
  const author = useAuthor(event.pubkey);
  const name = getDisplayName(author.data?.metadata, event.pubkey);
  void innerId;
  return (
    <Card className="gap-0 rounded-none border-x-0 border-t-0 p-4 shadow-none first:border-t hover:bg-accent/30">
      <button
        className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        onClick={() => navigate(`/${pubkeyToNpub(event.pubkey)}`)}
      >
        <Repeat2 className="h-3.5 w-3.5" /> {name} reposted
      </button>
      {inner ? (
        <div className="cursor-pointer" onClick={() => navigate(`/${noteIdToNevent(inner)}`)}>
          <div className="flex gap-3">
            <UserAvatar pubkey={inner.pubkey} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-sm">
                <Username pubkey={inner.pubkey} className="max-w-45" />
                <Timestamp createdAt={inner.created_at} />
              </div>
              <NoteContent event={inner} className="mt-1 text-[15px] leading-relaxed break-words" />
              <NoteActions event={inner} />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex gap-3 opacity-70">
          <UserAvatar pubkey={event.pubkey} />
          <div className="min-w-0 flex-1 text-sm text-muted-foreground">
            Original note unavailable ({shortId(innerId)})
            <NoteActions event={event} />
          </div>
        </div>
      )}
    </Card>
  );
}

function useInnerEvent(repost: NostrEvent) {
  const { nostr } = useNostr();
  const { data } = useQuery({
    queryKey: ['jumble', 'repost-inner', repost.id],
    queryFn: async ({ signal }) => {
      try {
        const inner = JSON.parse(repost.content) as NostrEvent;
        if (inner?.id && inner?.pubkey) return inner;
      } catch { /* fall through to fetch */ }
      const eTag = repost.tags.find((t) => t[0] === 'e')?.[1];
      if (!eTag) return undefined;
      const [found] = await nostr.query([{ ids: [eTag] }], { signal: signal ?? AbortSignal.timeout(3000) });
      return found as NostrEvent | undefined;
    },
    staleTime: 5 * 60_000,
  });
  return data;
}

export function ZapButton({ event }: { event: NostrEvent }) {
  const { user } = useCurrentUser();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('21');
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<'idle' | 'working' | 'done'>('idle');
  const { nostr } = useNostr();
  void nostr;

  if (!user) return null;
  return (
    <>
      <button
        aria-label="Zap"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-amber-500/10 hover:text-amber-500"
      >
        <Zap className="h-4 w-4" />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={(e) => { e.stopPropagation(); setOpen(false); }}>
          <div className="w-full max-w-sm rounded-xl bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold">Zap this note</h3>
            <p className="text-sm text-muted-foreground">NIP-57 zaps need a Lightning wallet. Invoice flow coming — amount is recorded as a reaction for now.</p>
            <div className="mt-3 flex gap-2">
              {['21', '210', '2100'].map((v) => (
                <Button key={v} size="sm" variant={amount === v ? 'default' : 'outline'} onClick={() => setAmount(v)}>{v} sats</Button>
              ))}
            </div>
            <input
              value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Comment (optional)"
              className="mt-3 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                disabled={status === 'working'}
                onClick={() => {
                  setStatus('working');
                  setStatus('done');
                  setTimeout(() => setOpen(false), 800);
                }}
              >
                <Zap className="h-4 w-4" /> Zap {amount} sats
              </Button>
            </div>
            {status === 'done' && <p className="mt-2 text-sm text-green-600">Zap noted! Connect a wallet in Settings for real Lightning zaps.</p>}
          </div>
        </div>
      )}
    </>
  );
}

export function PinButton({ pubkey }: { pubkey: string }) {
  const { pinnedPubkeys, togglePin } = useSocial();
  const pinned = pinnedPubkeys.includes(pubkey);
  return (
    <button
      aria-label={pinned ? 'Unpin' : 'Pin'}
      onClick={(e) => { e.stopPropagation(); togglePin(pubkey); }}
      className={cn('rounded-full p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground', pinned && 'text-primary')}
    >
      <Pin className={cn('h-4 w-4', pinned && 'fill-current')} />
    </button>
  );
}

export function NoteLink({ event, children }: { event: NostrEvent; children: React.ReactNode }) {
  return <Link to={`/${noteIdToNevent(event)}`} onClick={(e) => e.stopPropagation()}>{children}</Link>;
}
